import { ReactNode } from 'react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Lock } from 'lucide-react';
import { SaveButton } from '@/components/ui/SaveButton';

interface ProducerSectionHeaderProps {
  title: string;
  description?: string;
  statusBadge?: ReactNode;
  onSave?: () => void;
  isSaving?: boolean;
  isDirty?: boolean;
  /** If true, hide save button and show read-only badge */
  isReadOnly?: boolean;
  /** Custom tooltip for disabled button */
  saveDisabledTooltip?: string;
}

export function ProducerSectionHeader({
  title,
  description,
  statusBadge,
  onSave,
  isSaving,
  isDirty,
  isReadOnly = false,
  saveDisabledTooltip,
}: ProducerSectionHeaderProps) {
  const showSaveButton = onSave && !isReadOnly;

  return (
    <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
      <div>
        <div className="flex items-center gap-3 mb-1">
          <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
          {statusBadge}
          {isReadOnly && (
            <Badge variant="outline" className="text-muted-foreground gap-1">
              <Lock className="h-3 w-3" />
              Endast läsning
            </Badge>
          )}
        </div>
        {description && (
          <p className="text-muted-foreground">{description}</p>
        )}
      </div>

      {showSaveButton && (
        <div className="flex items-center gap-2">
          {isDirty && (
            <span className="text-xs text-amber-600 font-medium">
              Osparade ändringar
            </span>
          )}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0}>
                  <SaveButton
                    onClick={onSave}
                    disabled={!isDirty}
                    isSaving={!!isSaving}
                  >
                    Spara ändringar
                  </SaveButton>
                </span>
              </TooltipTrigger>
              {saveDisabledTooltip && !isDirty && (
                <TooltipContent>
                  <p>{saveDisabledTooltip}</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      )}
    </div>
  );
}
